import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/App";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { 
  User, Mail, Key, Info, Crown, Check, ArrowRight,
  Sparkles, Zap, Building2, Rocket, Infinity, CreditCard
} from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_f50ba61d-1f02-4136-b6b8-0e3c71c5b623/artifacts/e4hujy2b_sologo-aitools-bgremover-trimmed.png";

const plans = [
  { id: "solo", icon: Sparkles, price: 0, sites: 1, color: "#6B7280" },
  { id: "creator", icon: Zap, price: 9.99, sites: 5, color: "#20C997" },
  { id: "pro", icon: Crown, price: 24.99, sites: 20, color: "#1FB6FF", popular: true },
  { id: "business", icon: Building2, price: 69.99, sites: 100, color: "#1E2A5A" },
  { id: "agency", icon: Rocket, price: 99, sites: 300, color: "#8B5CF6" },
  { id: "infinity", icon: Infinity, price: 199, sites: -1, color: "#F59E0B" },
];

export default function SettingsPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const defaultTab = searchParams.get('tab') || 'profile';
  
  // Get user plan (default to 'solo' if not set)
  const userPlan = user?.plan || 'solo';
  const currentPlanData = plans.find(p => p.id === userPlan) || plans[0];

  const handleChangePlan = (planId) => {
    // This would normally call the backend to change the plan
    toast.success(t('settings.subscription.changePlan') + `: ${planId}`);
  };

  return (
    <div className="space-y-8 animate-fade-in" data-testid="settings-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold font-heading text-[#1E2A5A]">{t('settings.title')}</h1>
        <p className="text-[#6B7280] mt-1">{t('settings.subtitle')}</p>
      </div>

      <Tabs defaultValue={defaultTab} className="w-full">
        <TabsList className="grid w-full max-w-md grid-cols-3 rounded-xl bg-[#F5F7FA]">
          <TabsTrigger value="profile" className="rounded-lg data-[state=active]:bg-white data-[state=active]:text-[#1E2A5A]">
            <User className="w-4 h-4 mr-2" />
            {t('settings.profile.title')}
          </TabsTrigger>
          <TabsTrigger value="subscription" className="rounded-lg data-[state=active]:bg-white data-[state=active]:text-[#1E2A5A]">
            <CreditCard className="w-4 h-4 mr-2" />
            {t('settings.subscription.title')}
          </TabsTrigger>
          <TabsTrigger value="wordpress" className="rounded-lg data-[state=active]:bg-white data-[state=active]:text-[#1E2A5A]">
            <Key className="w-4 h-4 mr-2" />
            WordPress
          </TabsTrigger>
        </TabsList>

        {/* Profile Tab */}
        <TabsContent value="profile" className="mt-6 space-y-6 max-w-2xl">
          <Card className="border-0 bg-white rounded-2xl soft-shadow">
            <CardHeader>
              <CardTitle className="font-heading text-lg text-[#1E2A5A] flex items-center gap-2">
                <User className="w-5 h-5" />
                {t('settings.profile.title')}
              </CardTitle>
              <CardDescription className="text-[#6B7280]">{t('settings.profile.description')}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-[#1E2A5A]">{t('settings.profile.name')}</Label>
                <Input
                  id="name"
                  value={user?.name || ""}
                  disabled
                  className="bg-[#F5F7FA] rounded-xl border-gray-200"
                  data-testid="profile-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email" className="text-[#1E2A5A]">{t('settings.profile.email')}</Label>
                <Input
                  id="email"
                  value={user?.email || ""}
                  disabled
                  className="bg-[#F5F7FA] rounded-xl border-gray-200"
                  data-testid="profile-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-[#1E2A5A]">{t('settings.profile.memberSince')}</Label>
                <Input
                  value={user?.created_at ? new Date(user.created_at).toLocaleDateString('fr-FR', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric'
                  }) : ""}
                  disabled
                  className="bg-[#F5F7FA] rounded-xl border-gray-200"
                />
              </div>
            </CardContent>
          </Card>

          {/* About */}
          <Card className="border-0 bg-white rounded-2xl soft-shadow">
            <CardHeader>
              <CardTitle className="font-heading text-lg text-[#1E2A5A]">{t('settings.about.title')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-3 mb-4">
                <img src={LOGO_URL} alt="Diffuzo" className="h-8" />
              </div>
              <p className="text-sm text-[#6B7280]">
                {t('settings.about.description')}
              </p>
              <p className="text-sm text-[#6B7280] mt-2 italic">
                {t('settings.about.slogan')}
              </p>
              <p className="text-xs text-[#6B7280] mt-4">
                {t('settings.about.version')} 1.0.0
              </p>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Subscription Tab */}
        <TabsContent value="subscription" className="mt-6 space-y-6">
          {/* Current Plan */}
          <Card className="border-0 bg-white rounded-2xl soft-shadow">
            <CardHeader>
              <CardTitle className="font-heading text-lg text-[#1E2A5A] flex items-center gap-2">
                <Crown className="w-5 h-5" style={{ color: currentPlanData.color }} />
                {t('settings.subscription.currentPlan')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between p-6 bg-gradient-to-r from-[#1E2A5A] to-[#20C997] rounded-2xl text-white">
                <div className="flex items-center gap-4">
                  <div 
                    className="w-16 h-16 rounded-2xl flex items-center justify-center bg-white/20"
                  >
                    <currentPlanData.icon className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold font-heading">
                      {t(`pricing.plans.${userPlan}.name`)}
                    </h3>
                    <p className="text-white/70">
                      {currentPlanData.price === 0 
                        ? t('settings.subscription.free')
                        : `${currentPlanData.price}€ ${t('pricing.perMonth')}`
                      }
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm text-white/70">{t('settings.subscription.features')}</p>
                  <p className="text-lg font-semibold">
                    {currentPlanData.sites === -1 
                      ? t('settings.subscription.unlimited')
                      : t('settings.subscription.sites', { count: currentPlanData.sites })
                    }
                  </p>
                </div>
              </div>

              {/* Current plan features */}
              <div className="mt-6">
                <h4 className="text-sm font-medium text-[#6B7280] mb-3">{t('settings.subscription.features')}</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {t(`pricing.plans.${userPlan}.features`, { returnObjects: true }).map((feature, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-sm text-[#1E2A5A]">
                      <Check className="w-4 h-4 text-[#20C997]" />
                      {feature}
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Change Plan */}
          <Card className="border-0 bg-white rounded-2xl soft-shadow">
            <CardHeader>
              <CardTitle className="font-heading text-lg text-[#1E2A5A]">
                {t('settings.subscription.changePlan')}
              </CardTitle>
              <CardDescription className="text-[#6B7280]">
                {t('pricing.subtitle')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {plans.map((plan) => {
                  const Icon = plan.icon;
                  const isCurrentPlan = plan.id === userPlan;
                  const planTranslation = t(`pricing.plans.${plan.id}`, { returnObjects: true });
                  
                  return (
                    <div
                      key={plan.id}
                      className={`relative p-5 rounded-2xl border-2 transition-all ${
                        isCurrentPlan 
                          ? 'border-[#20C997] bg-[#20C997]/5' 
                          : 'border-gray-100 hover:border-gray-200'
                      }`}
                    >
                      {plan.popular && (
                        <Badge className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-[#1FB6FF] hover:bg-[#1FB6FF] rounded-full text-xs">
                          {t('pricing.popular')}
                        </Badge>
                      )}
                      
                      <div className="flex items-center gap-3 mb-3">
                        <div 
                          className="w-10 h-10 rounded-xl flex items-center justify-center"
                          style={{ backgroundColor: `${plan.color}15` }}
                        >
                          <Icon className="w-5 h-5" style={{ color: plan.color }} />
                        </div>
                        <div>
                          <h4 className="font-semibold text-[#1E2A5A]">{planTranslation.name}</h4>
                          <p className="text-xs text-[#6B7280]">
                            {plan.sites === -1 
                              ? t('settings.subscription.unlimited')
                              : t('settings.subscription.sites', { count: plan.sites })
                            }
                          </p>
                        </div>
                      </div>

                      <div className="mb-4">
                        {plan.price === 0 ? (
                          <span className="text-xl font-bold text-[#1E2A5A]">{t('pricing.free')}</span>
                        ) : (
                          <span className="text-xl font-bold text-[#1E2A5A]">
                            {plan.price}€<span className="text-sm font-normal text-[#6B7280]">{t('pricing.perMonth')}</span>
                          </span>
                        )}
                      </div>

                      {isCurrentPlan ? (
                        <Button disabled className="w-full rounded-xl bg-gray-100 text-[#6B7280]">
                          {t('pricing.currentPlan')}
                        </Button>
                      ) : (
                        <Button 
                          onClick={() => handleChangePlan(plan.id)}
                          className={`w-full rounded-xl ${
                            plans.findIndex(p => p.id === plan.id) > plans.findIndex(p => p.id === userPlan)
                              ? 'bg-[#1E2A5A] hover:bg-[#20C997]'
                              : 'bg-white border border-gray-200 text-[#1E2A5A] hover:bg-[#F5F7FA]'
                          }`}
                        >
                          {plans.findIndex(p => p.id === plan.id) > plans.findIndex(p => p.id === userPlan)
                            ? t('settings.subscription.upgrade')
                            : t('settings.subscription.downgrade')
                          }
                        </Button>
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="mt-6 text-center">
                <Link to="/pricing" className="text-sm text-[#20C997] hover:text-[#1E2A5A] transition-colors">
                  {t('pricing.yearlyDiscount')}
                  <ArrowRight className="w-4 h-4 inline ml-1" />
                </Link>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* WordPress Tab */}
        <TabsContent value="wordpress" className="mt-6 space-y-6 max-w-2xl">
          <Card className="border-0 bg-white rounded-2xl soft-shadow">
            <CardHeader>
              <CardTitle className="font-heading text-lg text-[#1E2A5A] flex items-center gap-2">
                <Key className="w-5 h-5" />
                {t('settings.wordpress.title')}
              </CardTitle>
              <CardDescription className="text-[#6B7280]">{t('settings.wordpress.description')}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-[#F5F7FA] rounded-xl p-5 space-y-3">
                <div className="flex items-start gap-3">
                  <Info className="w-5 h-5 text-[#1E2A5A] mt-0.5" />
                  <div>
                    <h4 className="font-medium text-[#1E2A5A] mb-1">{t('settings.wordpress.appPassword')}</h4>
                    <p className="text-sm text-[#6B7280]">
                      {t('settings.wordpress.instructions')}
                    </p>
                  </div>
                </div>
                <ol className="list-decimal list-inside space-y-2 text-sm text-[#6B7280] ml-8">
                  <li>{t('settings.wordpress.step1')}</li>
                  <li>{t('settings.wordpress.step2')}</li>
                  <li>{t('settings.wordpress.step3')}</li>
                  <li>{t('settings.wordpress.step4')}</li>
                  <li>{t('settings.wordpress.step5')}</li>
                </ol>
              </div>
              
              <Separator className="bg-gray-100" />
              
              <div className="bg-[#F5F7FA] rounded-xl p-5 space-y-3">
                <div className="flex items-start gap-3">
                  <Info className="w-5 h-5 text-amber-500 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-[#1E2A5A] mb-1">{t('settings.wordpress.requirements')}</h4>
                    <ul className="list-disc list-inside space-y-1 text-sm text-[#6B7280]">
                      <li>{t('settings.wordpress.req1')}</li>
                      <li>{t('settings.wordpress.req2')}</li>
                      <li>{t('settings.wordpress.req3')}</li>
                      <li>{t('settings.wordpress.req4')}</li>
                    </ul>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
